import React from "react";
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const ITEM_HEIGHT = 48;

type ActionItem = {
  title: string;
  onClick: (row: any) => void;
  icon?: React.ReactNode;
  className?: string;
};

function RowActionsMenu({ items, row }: { items: ActionItem[]; row: any }) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  return (
    <div>
      <IconButton aria-label="more" onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            style: { maxHeight: ITEM_HEIGHT * 4.5, width: '20ch' },
          },
        }}
      >
        {items?.map((item) => (
          <MenuItem
            key={item.title}
            onClick={() => {
              handleClose();
              item.onClick(row);
            }}
            className={item.className || ''}
          >
            <span className="flex items-center gap-2">
              {item.icon}
              <span>{item.title}</span>
            </span>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}


type Column = {
    key: string;
    label: string;
    render?: (row:any) => void
};

interface ListProps {
    columns: Column[];
    data: Record<string, React.ReactNode>[];
numbered?: boolean;
    fullSpanKey?: string;
    actions?: ActionItem[];
}

const List: React.FC<ListProps> = ({ columns, data, numbered = false, fullSpanKey, actions }) => {
    // Compute grid column sizes
    // const gridTemplate = `${numbered ? "50px " : ""}${columns
    //     ?.map(col => (col.key === fullSpanKey ? "1fr" : "auto"))
    //     ?.join(" ")}`;

    return (
        <div className="overflow-x-auto rounded-lg">
            <table className="min-w-full border-collapse">
                <thead className="bg-gray-100">
                    <tr>
                        {numbered && <th className="px-4 py-4 text-gray-400 font-bold text-left">#</th>}
                        {columns?.map(col => (
                            <th key={col.key} className="px-4 py-4 text-gray-400 font-bold text-left">
                                {col.label}
                            </th>
                        ))}
                        {actions && actions.length > 0 && (
                          <th className="px-4 py-4 text-gray-400 font-bold text-left">Actions</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {data?.map((row, rowIndex) => (
                        <tr key={rowIndex} className="border-b border-dashed border-gray-300">
                            {numbered && <td className="px-4 py-4">{rowIndex + 1}</td>}
                            {columns?.map(col => {
                                if (col.key === fullSpanKey) {
                                    return (
                                        <td
                                            key={col.key}
                                            colSpan={columns.length - (numbered ? 0 : 0)}
                                            className="px-4 py-4"
                                        >
                                            {row[col.key]}
                                        </td>
                                    );
                                }
                                return (
                                    <td key={col.key} className="px-4 py-4">
                                        {col?.render?.(row) || row[col.key]}
                                    </td>
                                );
                            })}
                            {actions && actions.length > 0 && (
                              <td className="px-4 py-4">
                                <RowActionsMenu items={actions} row={row} />
                              </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default List;
